import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import TiktokOpsPager from '@/tiktok-platform/components/TiktokOpsPager';

const SESSION_PAGE_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

/** 场次状态（对齐平台 sessionStatus） */
function sessionStatusTone(labelOrCode) {
  const raw = String(labelOrCode ?? '').trim();
  if (raw === '1' || raw === '未开始') return 'pending';
  if (raw === '2' || raw === '进行中') return 'success';
  if (raw === '3' || raw === '已结束') return 'offline';
  if (raw === '4' || raw === '报名失败') return 'failed';
  if (raw === '5' || raw === '已售罄') return 'appeal';
  if (raw === '6' || raw === '已下线') return 'offline';
  return 'pending';
}

/** 报名状态（与报名记录列表 enrollStatusTone 一致：4=已分配→绿） */
function enrollStatusTone(labelOrCode) {
  const raw = String(labelOrCode ?? '').trim();
  if (raw === '1' || raw.includes('报名中')) return 'pending';
  if (raw === '2' || raw.includes('报名失败')) return 'failed';
  if (raw === '3' || raw.includes('待分配')) return 'pending';
  if (raw === '4' || raw.includes('已分配') || raw.includes('报名成功')) return 'success';
  if (raw === '5' || raw.includes('已结束')) return 'offline';
  if (raw === '6' || raw.includes('已下线')) return 'offline';
  return sessionStatusTone(raw);
}

/**
 * 报名记录 · 活动场次全部列表弹窗（表格 + 表体滚动 + 底部分页）
 */
export default function MarketingEnrollSessionsModal({
  open = false,
  title = '',
  sessions = [],
  enrollStatusLabel = '',
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const list = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions],
  );

  useEffect(() => {
    if (!open) return undefined;
    setPage(1);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, list.length]);

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  if (!open) return null;

  return createPortal(
    <div
      className="tko-batch-modal tko-marketing-sessions-modal"
      role="presentation"
      onClick={onClose}
    >
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className="tko-batch-modal__panel tko-marketing-sessions-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-marketing-sessions-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tko-batch-modal__head tko-marketing-sessions-modal__head">
          <div>
            <h2 id="tko-marketing-sessions-title">{title || t('marketing.enSessionsTitle')}</h2>
            <p className="tko-batch-modal__hint" style={{ margin: '4px 0 0' }}>
              {t('marketing.enSessionCount', { count: total })}
            </p>
          </div>
          <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="tko-marketing-sessions-modal__main">
          {total === 0 ? (
            <p className="tko-batch-modal__hint tko-marketing-sessions-modal__empty">{fixedUi("暂无场次")}</p>
          ) : (
            <div className="tko-marketing-sessions-modal__table-wrap">
              <div className="tko-marketing-sessions-modal__scroll" role="region" aria-label={t('marketing.enSessionList')}>
                <table className="tko-marketing-sessions-modal__table">
                  <thead>
                    <tr>
                      <th scope="col">{fixedUi("站点")}</th>
                      <th scope="col">{fixedUi("活动时间")}</th>
                      <th scope="col" className="tko-marketing-sessions-modal__num">{fixedUi("持续天数")}</th>
                      <th scope="col">{fixedUi("场次状态")}</th>
                      <th scope="col">{fixedUi("报名状态")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((session, idx) => {
                      const enrollText = session.enrollStatusLabel || enrollStatusLabel || '—';
                      const sessionText = session.sessionStatusLabel || '—';
                      return (
                        <tr key={session.sessionId || `${safePage}-${idx}`}>
                          <td>{session.siteName || '—'}</td>
                          <td>
                            {session.timeLabel
                              || [session.startTime, session.endTime].filter(Boolean).join(' - ')
                              || '—'}
                          </td>
                          <td className="tko-marketing-sessions-modal__num">
                            {session.durationDays != null ? session.durationDays : '—'}
                          </td>
                          <td>
                            <span className={`tko-records__status tko-records__status--${sessionStatusTone(session.sessionStatus ?? sessionText)}`}>
                              {fixedUi(sessionText)}
                            </span>
                          </td>
                          <td>
                            <span className={`tko-records__status tko-records__status--${enrollStatusTone(session.enrollStatus ?? enrollText)}`}>
                              {fixedUi(enrollText)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="tko-marketing-sessions-modal__pager">
                <TiktokOpsPager
                  page={safePage}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  pageSizeOptions={SESSION_PAGE_SIZE_OPTIONS}
                  ariaLabel={t('marketing.enSessionPager')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
